'use client'

import { useEffect, useRef, useState } from 'react'

import type { YouTubePlayer, YouTubePlayerEvent } from '@/blocks/PlaylistBlock/types'
import type { RefObject } from 'react'

type UseYouTubePlaylistPlayerArgs = {
  videoId: string | null
  hasUploadMedia: boolean
  onNearComplete: () => void
  onStateChange: (event: { data: number }) => void
}

type NearCompleteTick = {
  alreadyFired: boolean
  loadedVideoId: string | null
  expectedVideoId: string | null
  duration?: number
  currentTime?: number
}

function isPlayerMounted(player: YouTubePlayer | null): boolean {
  try {
    return Boolean(player?.getIframe?.()?.isConnected)
  } catch {
    return false
  }
}

function ensureMountNode(container: HTMLDivElement): HTMLDivElement {
  const existing = container.firstElementChild
  if (existing instanceof HTMLDivElement) return existing
  const mount = document.createElement('div')
  mount.className = 'size-full'
  container.replaceChildren(mount)
  return mount
}

function getLoadedVideoId(player: YouTubePlayer): string | null {
  try {
    return player.getVideoData?.()?.video_id ?? null
  } catch {
    return null
  }
}

function clearPlayerInterval(player: YouTubePlayer | null) {
  if (player?.__interval) {
    clearInterval(player.__interval)
    player.__interval = undefined
  }
}

function destroyYouTubePlayer(player: YouTubePlayer | null) {
  clearPlayerInterval(player)
  try {
    player?.destroy?.()
  } catch {
    // IFrame API throws when React already removed the embed node.
  }
}

/**
 * Returns whether a 90% watch tick should mark the expected clip complete.
 * Ignores stale time/duration from the previous video after `loadVideoById`.
 */
export function shouldMarkNearComplete({
  alreadyFired,
  loadedVideoId,
  expectedVideoId,
  duration,
  currentTime,
}: NearCompleteTick): boolean {
  if (alreadyFired || !expectedVideoId || loadedVideoId !== expectedVideoId) return false
  if (!duration || !currentTime) return false
  return currentTime / duration > 0.9
}

/**
 * Loads the YouTube IFrame API and owns the playlist embed player.
 * The React ref is a wrapper YouTube never replaces; the API swaps an inner
 * mount node for the iframe. Reuses `loadVideoById` when that iframe is still
 * in the document, starts 90% watch tracking only after the new clip is
 * playing, and always resets refs if destroy throws after React unmounts the embed.
 */
export function useYouTubePlaylistPlayer({
  videoId,
  hasUploadMedia,
  onNearComplete,
  onStateChange,
}: UseYouTubePlaylistPlayerArgs): { playerContainerRef: RefObject<HTMLDivElement | null> } {
  const playerRef = useRef<YouTubePlayer | null>(null)
  const playerContainerRef = useRef<HTMLDivElement | null>(null)
  const playerReadyRef = useRef(false)
  const nearCompleteFiredRef = useRef(false)
  const videoIdRef = useRef(videoId)
  const onNearCompleteRef = useRef(onNearComplete)
  const onStateChangeRef = useRef(onStateChange)

  useEffect(() => {
    videoIdRef.current = videoId
    onNearCompleteRef.current = onNearComplete
    onStateChangeRef.current = onStateChange
  }, [videoId, onNearComplete, onStateChange])

  const [ytReady, setYtReady] = useState(() => typeof window !== 'undefined' && !!window.YT?.Player)

  useEffect(() => {
    if (ytReady) return

    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      prev?.()
      setYtReady(true)
    }

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      document.body.appendChild(script)
    }
  }, [ytReady])

  useEffect(() => {
    const destroyPlayer = () => {
      destroyYouTubePlayer(playerRef.current)
      playerRef.current = null
      playerReadyRef.current = false
    }

    const attachInterval = (player: YouTubePlayer, expectedVideoId: string) => {
      clearPlayerInterval(player)
      player.__interval = setInterval(() => {
        if (
          shouldMarkNearComplete({
            alreadyFired: nearCompleteFiredRef.current,
            loadedVideoId: getLoadedVideoId(player),
            expectedVideoId,
            duration: player.getDuration?.(),
            currentTime: player.getCurrentTime?.(),
          })
        ) {
          nearCompleteFiredRef.current = true
          onNearCompleteRef.current()
        }
      }, 1000)
    }

    const handleStateChange = (event: { data: number; target: YouTubePlayer }) => {
      const expectedVideoId = videoIdRef.current
      if (
        event.data === window.YT?.PlayerState?.PLAYING &&
        expectedVideoId &&
        getLoadedVideoId(event.target) === expectedVideoId
      ) {
        attachInterval(event.target, expectedVideoId)
      }
      onStateChangeRef.current(event)
    }

    const container = playerContainerRef.current
    const containerInDom = Boolean(container?.isConnected)
    const shouldMount = Boolean(ytReady && videoId && !hasUploadMedia && containerInDom)
    const yt = window.YT

    if (!shouldMount || !yt?.Player || !container || !videoId) {
      destroyPlayer()
      return
    }

    nearCompleteFiredRef.current = false

    if (
      playerReadyRef.current &&
      playerRef.current?.loadVideoById &&
      isPlayerMounted(playerRef.current)
    ) {
      clearPlayerInterval(playerRef.current)
      playerRef.current.loadVideoById(videoId)
    } else {
      destroyPlayer()
      playerRef.current = new yt.Player(ensureMountNode(container), {
        videoId,
        playerVars: { rel: 0, playsinline: 1 },
        events: {
          onReady: (event: YouTubePlayerEvent) => {
            if (playerRef.current !== event.target) return
            playerReadyRef.current = true
          },
          onStateChange: handleStateChange,
        },
      })
    }

    return () => {
      clearPlayerInterval(playerRef.current)
    }
  }, [ytReady, videoId, hasUploadMedia])

  useEffect(() => {
    return () => {
      destroyYouTubePlayer(playerRef.current)
      playerRef.current = null
      playerReadyRef.current = false
    }
  }, [])

  return { playerContainerRef }
}
